<template>
    <!--
    We put the slotted trigger inside a <span> so we can:
      - Attach mouseenter/mouseleave listeners
      - Grab a ref for measuring its bounding rect
    Using "display: contents" means this <span> won't add any layout box.
  -->
    <span
        ref="trigger"
        :class="[common.triggerElement, props.triggerClass]"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
        @focus="showTooltip"
        @blur="hideTooltip"
    >
        <slot />
    </span>

    <teleport to="body">
        <Transition
            :enterActiveClass="common[`tooltip-${adjustedPosition}`]"
            :leaveActiveClass="`${common[`tooltip-${adjustedPosition}`]} ${common.reverse}`"
        >
            <div
                v-if="isVisible && $slots.content"
                ref="tooltip"
                :class="common.tooltip"
                :style="tooltipStyle"
                @mouseenter="onHoverTooltip"
                @mouseleave="hideTooltip"
            >
                <slot
                    :interfaceTarget="props.interfaceTarget"
                    name="content"
                />
            </div>
        </Transition>
    </teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import { useDetectShowEvent } from '@/composables/use-detect-show-interface-event'

import common from './Tooltip.module.css'
import { type Position, useTooltip } from './use-tooltip'

interface Props {
    position?: Position
    offset?: number
    interfaceTarget: string
    triggerClass?: string
}

const props = withDefaults(defineProps<Props>(), {
    position: 'top',
    offset: 6,
})

const emit = defineEmits<{ show: [external: boolean]; hide: [] }>()

const trigger = ref<HTMLElement | null>(null)
const tooltip = ref<HTMLElement | null>(null)

const { isVisible, showTooltip, hideTooltip, adjustedPosition, tooltipStyle, onHoverTooltip } = useTooltip({
    trigger,
    hideDelayMs: 100,
    tooltip,
    additionalOffset: props.offset,
    position: props.position,
})

const interfaceTargetPostfix = ref('')

function externalShowTooltip() {
    showTooltip()
    interfaceTargetPostfix.value = 'external-trigger'
}

defineExpose({ showTooltip: externalShowTooltip, hideTooltip })

const detectShowEvent = useDetectShowEvent()

watch(
    () => isVisible.value,
    () => {
        if (isVisible.value) {
            const interfaceTarget = interfaceTargetPostfix.value
                ? `${props.interfaceTarget}-${interfaceTargetPostfix.value}`
                : props.interfaceTarget

            detectShowEvent.trigger({
                id: interfaceTarget,
                interface_target: interfaceTarget,
            })
            emit('show', !!interfaceTargetPostfix.value)
        } else {
            interfaceTargetPostfix.value = ''
            emit('hide')
        }
    },
)
</script>
